import { Component, ErrorInfo, ReactNode } from "react";
import { Alert, Box, Button, Card, CardContent, Typography } from "@mui/material";
import { Refresh } from "@mui/icons-material";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
          <Card variant="outlined" sx={{ maxWidth: 480, width: "100%", borderRadius: 2 }}>
            <CardContent sx={{ textAlign: "center", p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Something went wrong
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                An unexpected error occurred while loading this view.
              </Typography>
              {this.state.error?.message && (
                <Alert severity="error" sx={{ textAlign: "left", mb: 2, fontSize: "0.8rem" }}>
                  {this.state.error.message}
                </Alert>
              )}
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={this.handleReload}
                sx={{ textTransform: "none", borderRadius: "8px" }}
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        </Box>
      );
    }
    return this.props.children;
  }
}
